import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Spinner } from 'react-bootstrap';
import { SetPasswordRequest } from '../../_core/_request.ts'; // Adjust path as needed

const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  // Validate URL parameters on load
  useEffect(() => {
    if (!token || !email) {
      setStatus('error');
      setMessage('Invalid password reset link. Missing token or email.');
    }
  }, [token, email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setStatus('error');
      setMessage('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setStatus('error');
      setMessage('Password must be at least 8 characters long.');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const response = await SetPasswordRequest({ 
        Email: email as string, 
        Token: token as string, 
        NewPassword: password 
      });
      
      setStatus('success');
      setMessage(response.data.message || 'Password successfully reset.');
    } catch (err: any) {
      setStatus('error');
      // The backend returns bad request messages like "Expired link" directly
      setMessage(err.response?.data || 'Failed to reset password. The link may be expired or invalid.');
    }
  };

  const variants = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 }
  };

  if (!token || !email) {
    return (
      <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="alert alert-danger shadow">
          {message} <br />
          <Link to="/forgot-password">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <motion.div
        className="card shadow p-4 w-100"
        style={{ maxWidth: '500px' }}
        initial="initial"
        animate="animate"
        variants={variants}
        transition={{ duration: 0.3 }}
      >
        <div className="card-body d-flex flex-column gap-3">
          <h2 className="card-title text-center text-primary mb-4">Create New Password</h2>
          
          {status === 'success' ? (
            <div className="text-center">
              <div className="alert alert-success">{message}</div>
              <button className="btn btn-primary mt-3 w-100" onClick={() => navigate('/login')}>
                Go to Login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="d-flex flex-column gap-3">
              <div>
                <label className="form-label">New Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={password}
                  required
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={status === 'loading'}
                  placeholder="At least 8 characters"
                />
              </div>

              <div>
                <label className="form-label">Confirm New Password</label>
                <input
                  type="password"
                  className={`form-control ${password && confirmPassword && password !== confirmPassword ? 'is-invalid' : ''}`}
                  value={confirmPassword}
                  required
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={status === 'loading'}
                />
              </div>

              {status === 'error' && <div className="text-danger small">{message}</div>}

              <button className="btn btn-primary mt-3" type="submit" disabled={status === 'loading' || !password || !confirmPassword}>
                {status === 'loading' ? (
                  <span><Spinner animation="border" size="sm" className="me-2" />Saving...</span>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default SetPassword;